import React from 'react';
import { ContractAnalysisEditorRef } from './ContractAnalysisEditor';
import { Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import '../styles/ContractAnalysisEditor.css';

interface ContractPreviewPatchProps {
  analysisResult: any;
  highlightedText: string;
  comments: any[];
  revisions: any[];
  isAddingComment: boolean;
  isAddingRevision: boolean;
  setSelectedText: (text: string) => void;
  setSelectedTextRange: (range: {start: number, end: number} | null) => void;
  setShowCommentDialog: (show: boolean) => void;
  setShowRevisionDialog: (show: boolean) => void;
  setNewRevisionText: (text: string) => void;
  mdxEditorRef: React.RefObject<ContractAnalysisEditorRef | null>;
  resolvedRiskIds?: Set<string>;
  riskRanges?: Array<{ start: number; end: number; idx: number }>;
  onQuickSelect?: (payload: { text: string; start: number; end: number; rect: { x: number; y: number; width: number; height: number } }) => void;
}

const ContractPreviewPatch: React.FC<ContractPreviewPatchProps> = ({
  analysisResult,
  highlightedText,
  comments,
  revisions,
  isAddingComment,
  isAddingRevision,
  setSelectedText,
  setSelectedTextRange,
  setShowCommentDialog,
  setShowRevisionDialog,
  setNewRevisionText,
  mdxEditorRef,
  resolvedRiskIds,
  riskRanges,
  onQuickSelect
}) => {

  // Render text with comments and revisions (same logic as SharedContractPage)
  const renderContractText = (originalText: string) => {
    if (!originalText) return originalText;

    let workingText = originalText;
    const positionOffsets: Array<{start: number, end: number, offset: number}> = [];

    // Apply accepted revisions
    const acceptedRevisions = revisions.filter(r => r.accepted === true).sort((a, b) => a.start - b.start);
    acceptedRevisions.forEach(revision => {
      const lengthDiff = revision.newText.length - (revision.end - revision.start);
      workingText = workingText.substring(0, revision.start) +
                    revision.newText +
                    workingText.substring(revision.end);
      positionOffsets.push({start: revision.start, end: revision.end, offset: lengthDiff});
    });

    // Adjust positions
    const adjustPosition = (pos: number): number => {
      let adjusted = pos;
      positionOffsets.forEach(offset => {
        if (pos > offset.end) {
          adjusted += offset.offset;
        }
      });
      return adjusted;
    };

    // If no annotations and no risks, return raw text
    const hasPendingRevisions = revisions.filter(r => r.accepted !== true).length > 0;
    const hasComments = comments.length > 0;
    const hasRisks = Array.isArray(analysisResult?.risk_factors) && analysisResult.risk_factors.length > 0;
    if (!hasPendingRevisions && !hasComments && !hasRisks) {
      return workingText;
    }

    const segments: Array<{
      text: string,
      type: 'normal' | 'comment' | 'revision' | 'risk',
      id?: string,
      newText?: string,
      reason?: string,
      accepted?: boolean | null,
      riskIdx?: number
    }> = [];
    let lastIndex = 0;

    const riskMarks = (riskRanges || []).map(r => ({
      type: 'risk' as const,
      adjustedStart: adjustPosition(r.start),
      adjustedEnd: adjustPosition(r.end),
      riskIdx: r.idx
    }));

    const allMarks = [
      ...comments.map(c => ({
        ...c,
        type: 'comment' as const,
        adjustedStart: adjustPosition(c.start),
        adjustedEnd: adjustPosition(c.end)
      })),
      ...revisions.filter(r => r.accepted !== true).map(r => ({
        ...r,
        type: 'revision' as const,
        adjustedStart: adjustPosition(r.start),
        adjustedEnd: adjustPosition(r.end)
      })),
      ...riskMarks
    ].sort((a, b) => a.adjustedStart - b.adjustedStart);

    allMarks.forEach(mark => {
      if (lastIndex < mark.adjustedStart) {
        segments.push({
          text: workingText.substring(lastIndex, mark.adjustedStart),
          type: 'normal'
        });
      }

      if (mark.type === 'comment') {
        segments.push({
          text: workingText.substring(mark.adjustedStart, mark.adjustedEnd),
          type: 'comment',
          id: mark.id
        });
      } else if (mark.type === 'revision') {
        segments.push({
          text: workingText.substring(mark.adjustedStart, mark.adjustedEnd),
          type: 'revision',
          id: mark.id,
          newText: mark.newText,
          reason: mark.reason,
          accepted: mark.accepted
        });
      } else if (mark.type === 'risk') {
        segments.push({
          text: workingText.substring(mark.adjustedStart, mark.adjustedEnd),
          type: 'risk',
          riskIdx: (mark as any).riskIdx
        });
      }

      lastIndex = mark.adjustedEnd;
    });

    if (lastIndex < workingText.length) {
      segments.push({
        text: workingText.substring(lastIndex),
        type: 'normal'
      });
    }

    // Helper: render normal text with risk highlights
    const renderWithRiskHighlights = (text: string, keyPrefix: string) => {
      if (!hasRisks || !text) return <span key={`${keyPrefix}-plain`}>{text}</span>;

      // Build list of phrases to highlight from risk factors (prefer clause_text)
      const phrases: Array<{ phrase: string, idx: number, kind: 'clause' | 'keyword' | 'selected' }> = [];
      // If the page requested a direct highlighted text, include it first to ensure visibility
      if (highlightedText && highlightedText.trim().length >= 3) {
        phrases.push({ phrase: highlightedText.trim(), idx: -1, kind: 'selected' });
      }
      analysisResult.risk_factors.forEach((rf: any, idx: number) => {
        const p = (rf?.clause_text || '').toString().trim();
        if (p && p.length >= 6) {
          phrases.push({ phrase: p, idx, kind: 'clause' });
        } else {
          // fallback keywords from category/description
          const source = `${rf?.category || ''} ${rf?.description || ''}`.toString();
          const tokens = source
            .split(/[^\p{L}\p{N}]+/u)
            .map(s => s.trim())
            .filter(s => s.length >= 6)
            .slice(0, 3);
          tokens.forEach(t => phrases.push({ phrase: t, idx, kind: 'keyword' }));
        }
      });

      if (phrases.length === 0) return <span key={`${keyPrefix}-plain`}>{text}</span>;

      // Create matches within this text for non-overlapping highlights
      const lc = text.toLowerCase();
      const matches: Array<{ start: number, end: number, idx: number, kind: string }> = [];
      phrases.forEach(({ phrase, idx, kind }) => {
        const needle = phrase.toLowerCase();
        let searchFrom = 0;
        while (searchFrom < lc.length) {
          const pos = lc.indexOf(needle, searchFrom);
          if (pos === -1) break;
          // prevent over-highlighting trivial whitespace
          if (needle.replace(/\s+/g, '').length >= 3) {
            matches.push({ start: pos, end: pos + needle.length, idx, kind });
          }
          searchFrom = pos + Math.max(1, needle.length);
        }
      });

      if (matches.length === 0) return <span key={`${keyPrefix}-plain`}>{text}</span>;

      // Sort and deoverlap matches
      matches.sort((a, b) => a.start - b.start);
      const compact: typeof matches = [];
      matches.forEach(m => {
        const last = compact[compact.length - 1];
        if (!last || m.start >= last.end) compact.push(m);
      });

      const out: React.ReactNode[] = [];
      let cursor = 0;
      compact.forEach((m, i) => {
        if (cursor < m.start) {
          out.push(<span key={`${keyPrefix}-n-${i}`}>{text.substring(cursor, m.start)}</span>);
        }
        const riskText = text.substring(m.start, m.end);
        const isResolved = (m.idx >= 0) && ((resolvedRiskIds && resolvedRiskIds.has(`risk-${m.idx}`)) ||
                           (analysisResult.risk_factors?.[m.idx]?.resolvedByRevision));
        out.push(
          <span
            key={`${keyPrefix}-r-${i}`}
            className="risk-highlight"
            style={{
              backgroundColor: isResolved ? '#dcedc8' : '#fff59d',
              borderBottom: `2px solid ${isResolved ? '#66bb6a' : '#fbc02d'}`,
              cursor: 'pointer'
            }}
            title="Klikněte pro přidání revize k tomuto riziku"
            onClick={(e) => {
              const el = e.currentTarget as HTMLElement;
              const root = el.closest('.contract-formatted-content, .contract-text-content') as Element | null;
              const selected = el.textContent || '';
              if (root && selected) {
                const pre = document.createRange();
                pre.selectNodeContents(root);
                pre.setEnd(el, 0);
                const start = pre.toString().length;
                const end = start + selected.length;
                setSelectedText(selected);
                setSelectedTextRange({ start, end });
                setShowRevisionDialog(true);
              }
            }}
          >
            {riskText}
            {isResolved && (
              <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: '0.9rem', ml: 0.5, verticalAlign: 'text-bottom' }} />
            )}
          </span>
        );
        cursor = m.end;
      });
      if (cursor < text.length) out.push(<span key={`${keyPrefix}-tail`}>{text.substring(cursor)}</span>);
      return <>{out}</>;
    };

    return (
      <>
        {segments.map((segment, index) => {
          if (segment.type === 'comment') {
            return (
              <Box
                key={index}
                component="span"
                sx={{
                  backgroundColor: '#fffbf0',
                  borderBottom: '2px solid #ffc107',
                  padding: '2px 0',
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#fff3cd'
                  }
                }}
                title={comments.find(c => c.id === segment.id)?.comment}
              >
                {segment.text}
              </Box>
            );
          } else if (segment.type === 'revision') {
            return (
              <span key={index} data-revision-id={segment.id}>
                {segment.accepted === false ? (
                  <span>{segment.text}</span>
                ) : (
                  <>
                    <Box
                      component="span"
                      data-revision-id={segment.id}
                      className="revision-strikethrough"
                      sx={{
                        textDecoration: 'line-through',
                        color: '#d32f2f',
                        backgroundColor: '#ffebee',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        transition: 'all 0.5s ease-out'
                      }}
                    >
                      {segment.text}
                    </Box>
                    <Box
                      component="span"
                      data-revision-id={segment.id}
                      className="revision-replacement"
                      sx={{
                        color: '#2e7d32',
                        backgroundColor: '#e8f5e9',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        marginLeft: '4px',
                        fontWeight: 500,
                        transition: 'all 0.5s ease-out'
                      }}
                    >
                      {segment.newText}
                    </Box>
                  </>
                )}
              </span>
            );
          } else if (segment.type === 'risk') {
            const isResolved = (segment.riskIdx !== undefined) && ((resolvedRiskIds && resolvedRiskIds.has(`risk-${segment.riskIdx}`)) || (analysisResult?.risk_factors?.[segment.riskIdx]?.resolvedByRevision));
            return (
              <Box
                key={index}
                component="span"
                sx={{
                  backgroundColor: isResolved ? '#dcedc8' : '#fff59d',
                  borderBottom: `2px solid ${isResolved ? '#66bb6a' : '#fbc02d'}`,
                  padding: '0 0',
                  cursor: 'pointer'
                }}
                title="Klikněte pro přidání revize k tomuto riziku"
                onClick={(e) => {
                  const el = (e.currentTarget as HTMLElement);
                  const root = el.closest('.contract-formatted-content, .contract-text-content') as Element | null;
                  const selected = el.textContent || '';
                  if (root && selected) {
                    const pre = document.createRange();
                    pre.selectNodeContents(root);
                    pre.setEnd(el, 0);
                    const start = pre.toString().length;
                    const end = start + selected.length;
                    setSelectedText(selected);
                    setSelectedTextRange({ start, end });
                    setShowRevisionDialog(true);
                  }
                }}
              >
                {segment.text}
                {isResolved && (
                  <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: '0.9rem', ml: 0.5, verticalAlign: 'text-bottom' }} />
                )}
              </Box>
            );
          } else {
            // Normal text: render with risk highlights (fallback if no precomputed ranges)
            if (riskRanges && riskRanges.length > 0) {
              return <span key={index}>{segment.text}</span>;
            }
            return <span key={index}>{renderWithRiskHighlights(segment.text, `seg-${index}`)}</span>;
          }
        })}
      </>
    );
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (!selection || !selection.toString().trim()) return;
    const selected = selection.toString();
    const range = selection.getRangeAt(0);
    const preSelectionRange = range.cloneRange();

    const containerElement = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? (range.commonAncestorContainer as any).parentElement
      : (range.commonAncestorContainer as Element);
    const rootContainer = containerElement?.closest('.contract-formatted-content, .contract-text-content');
    if (!rootContainer) return;

    preSelectionRange.selectNodeContents(rootContainer as any);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    const start = preSelectionRange.toString().length;
    const end = start + selected.length;

    // When in explicit add modes, trigger dialogs immediately
    if (isAddingComment || isAddingRevision) {
      setSelectedText(selected);
      setSelectedTextRange({ start, end });
      if (isAddingComment) setShowCommentDialog(true);
      else if (isAddingRevision) setShowRevisionDialog(true);
      return;
    }

    // Otherwise, notify parent for quick actions toolbar
    if (onQuickSelect) {
      const rect = range.getBoundingClientRect();
      onQuickSelect({ text: selected, start, end, rect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height } });
    }
  };

  // Check if we have formatted HTML to display (1:1 PDF/Word rendering)
  if (analysisResult?.formatted_html && analysisResult.formatted_html.trim()) {
    return (
      <div style={{
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        backgroundColor: '#f5f5f5',
        maxHeight: '70vh',
        overflow: 'auto',
        padding: '20px',
        cursor: (isAddingComment || isAddingRevision) ? 'text' : 'default',
        userSelect: (isAddingComment || isAddingRevision) ? 'text' : 'auto'
      }}>
        <div
          className="contract-formatted-content"
          onMouseUp={handleTextSelection}
          style={{
            transformOrigin: 'top left',
            minHeight: '100%',
            backgroundColor: 'white',
            fontFamily: '"Times New Roman", Times, serif',
            fontSize: '12pt',
            lineHeight: '1.5',
            color: '#000000',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            textAlign: 'left'
          }}
        >
          {/* If we have original_text, render it with comments and revisions, otherwise fallback to HTML */}
          {analysisResult?.original_text ?
            renderContractText(analysisResult.original_text) :
            <div dangerouslySetInnerHTML={{ __html: analysisResult.formatted_html }} />
          }
        </div>
      </div>
    );
  }
  
  // Fallback to original text if no formatted HTML
  if (analysisResult?.original_text && analysisResult.original_text.trim()) {
    return (
      <div style={{
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        backgroundColor: 'white',
        maxHeight: '70vh',
        overflow: 'auto',
        cursor: (isAddingComment || isAddingRevision) ? 'text' : 'default',
        userSelect: (isAddingComment || isAddingRevision) ? 'text' : 'auto'
      }}>
        <div
          className="contract-text-content"
          onMouseUp={handleTextSelection}
          style={{
            padding: '40px 60px',
            fontFamily: '"Times New Roman", Times, serif',
            fontSize: '12pt',
            lineHeight: '1.5',
            color: '#000000',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            textAlign: 'left'
          }}
        >
          {renderContractText(analysisResult.original_text)}
        </div>
      </div>
    );
  }

  // Fallback for when there's no text
  return (
    <div style={{ 
      padding: '40px', 
      textAlign: 'center', 
      color: '#666',
      border: '2px dashed #ddd',
      borderRadius: '8px',
      backgroundColor: '#fafafa'
    }}>
      <p>📄</p>
      <p>Načítání textu smlouvy...</p>
    </div>
  );
};

export default ContractPreviewPatch;
